import React, { createContext, useState, useContext, useEffect } from "react";
import { login as apiLogin, verifyMe } from "../lib/api"; // Import the login and verify functions

// 1. Create the context
const AuthContext = createContext(null);

// 2. Create the Provider component
export const AuthProvider = ({ children }) => {
  // Initialize token and user from localStorage to keep state and storage consistent
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(true);

  // Effect to verify token and refresh user info on token changes
  useEffect(() => {
    const checkLoggedIn = async () => {
      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      try {
        // Pass the token if your API requires it for verification
        const verifiedUser = await verifyMe(token);
        setUser(verifiedUser);
      } catch (error) {
        console.error("Token verification failed:", error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };
    checkLoggedIn();
  }, [token]);

  // Sync token state to localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, [token]);

  // Sync user state to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  // Login function that calls the API and updates state
  const login = async (email, password) => {
    try {
      // The API response includes 'token' and 'user'
      const { token: newToken, user: newUser } = await apiLogin({
        email,
        password,
      });

      // Update state which synchronizes localStorage via useEffect hooks
      setToken(newToken);
      setUser(newUser);
    } catch (error) {
      console.error("Login failed:", error);
      throw error; // Re-throw error to be handled by the login form
    }
  };

  // Logout function clears state and localStorage
  const logout = () => {
    setToken(null);
    setUser(null);
  };

  // New: Refresh authenticated user info from backend
  const refreshUser = async () => {
    if (!token) return;
    try {
      const verifiedUser = await verifyMe(token);
      setUser(verifiedUser);
    } catch (error) {
      console.error("Refreshing user failed:", error);
      logout();
    }
  };

  // Provide user, token, authentication status and auth methods
  const value = {
    user,
    token,
    isAuthenticated: !!user, // Use user presence to confirm authenticated status
    isLoading,
    login,
    logout,
    refreshUser, // Export refreshUser to components
  };

  // Avoid rendering children until loading completes
  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

// 3. Create the custom hook to use the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
